// 檔名：http_func.tsx
// 功能概觀：
//     1. http_get：每個頁面初始化時，自動呼叫 API 優先獲取特定資料
//     2. http_request：頁面初始化後，呼叫剩下的 API，可能會隨著路徑變化（如 cluster 名稱、workspace 名稱）重複呼叫
//     3. destroy_worker：頁面切換或變動時，需要銷毀所有或特定類別的 Web Worker，以免滯留記憶體造成 Memory Leak 問題

import WebWorker from "./webworker";
import { namespaces_type_item } from "../contexts/namespaces";
import { workspaces_type_item } from "../contexts/workspaces";
import { quota_type, quota_type_item } from "../contexts/quota-management";
import { nodes_type, nodes_type_item } from "../contexts/nodes";
import { cluster_info_type } from "../contexts/cluster-info";
import { node_info_type } from "../contexts/node_info";
import { namespace_info_type_new } from "../contexts/namespace-info";
import { workspace_info_type } from "../contexts/workspace-info";
import { storage_classes_type_item } from "../contexts/storage-classes";
import { dashboard_item } from "../contexts/monitor-lists";
import { service_components_type_item } from "../contexts/service-component";
import { notification } from "antd";
import { IconType } from "antd/lib/notification/index";
import { app_item } from "../pages/applications/applications";
import { metric_values_new } from "./function/interfaces";
import { workload_type } from "../contexts/workloads";
import { pods_type_item } from "../contexts/pods";
import { log_out } from "../components/top-navbar/top-navbar";
import { clusters_type_item } from "../contexts/clusters";

// ██╗███╗   ██╗████████╗███████╗██████╗ ███████╗ █████╗  ██████╗███████╗
// ██║████╗  ██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝██╔══██╗██╔════╝██╔════╝
// ██║██╔██╗ ██║   ██║   █████╗  ██████╔╝█████╗  ███████║██║     █████╗
// ██║██║╚██╗██║   ██║   ██╔══╝  ██╔══██╗██╔══╝  ██╔══██║██║     ██╔══╝
// ██║██║ ╚████║   ██║   ███████╗██║  ██║██║     ██║  ██║╚██████╗███████╗
// ╚═╝╚═╝  ╚═══╝   ╚═╝   ╚══════╝╚═╝  ╚═╝╚═╝     ╚═╝  ╚═╝ ╚═════╝╚══════╝
//                型          態          宣          告

export interface response_type {
  error?: string;
  message?: string;
  path?: string;
  status?: number;
  timestamp?: string;
  kind?: string;
  items?: object[];
  code?: number;
  index: number;
  reason?: string;
  config?: string;
  content?: string;
}

interface left_menu_item {
  key: string;
}

interface worker_type {
  postMessage: Function;
  addEventListener: Function;
  removeEventListener: Function;
  terminate: Function;
}

export interface datacenter_url {
  [key: string]: { internalUrl?: string; externalUrl?: string };
}

// type log_out_type = (
//   props: any,
//   env?: datacenter_url,
//   history?: any,
//   redirect_url?: string,
//   formatMessage?: any,
//   custom_log_out_path?: string,
//   datacenter_name?: string
// ) => Promise<void>;

const workers: { key: string; worker: WebWorker; listener: Function }[] = [];

export const openNotification = (type: IconType, title?: string, msg?: string): void => {
  notification[type]({
    message: title ? title : "",
    description: msg ? msg : "",
  });
};

// HTTP GET 獲取資料
// 參數說明：

// 必填：
// func_name: 識別用的標籤，可自訂名稱，供 API 回傳值處理時識別用
// funcs: API 回傳值需要更新在哪個列表，該列表 context 的 set 函數
// role: 目前的角色名稱
// subscription_id: 目前的訂閱號 ID
// datacenter_name: 目前的 datacneter 名稱 (datacentercode)

// 選填：
// cluster_name: 目前的集群名稱
// workspace_name: 目前的 workspace 名稱
// namespace_name: 目前的 namespace 名稱
// dont_load_clusters: 每次執行http_get，都會呼叫集群列表API，若不要呼叫就設為true

export function http_get(
  func_name: string,
  funcs: (string | React.Dispatch<React.SetStateAction<any>>)[],
  role: string,
  subscription_id: string | undefined,
  datacenter_name: string,
  cluster_name?: string,
  workspace_name?: string,
  namespace_name?: string,
  dont_load_clusters?: boolean,
  set_cluster_info_display?: React.Dispatch<React.SetStateAction<string[] | undefined>>,
  unit_provide?: string
): Promise<unknown> {
  destroy_worker(func_name);
  return new Promise((resolve: (value?: unknown) => void): void => {
    // 初始化web worker
    const wasm: worker_type = new WebWorker();
    // 傳遞訊息給web worker，告訴web worker開始執行
    wasm.postMessage(
      JSON.stringify({
        func_name,
        origin: window.location.origin,
        role,
        datacenter_name,
        cluster_name,
        workspace_name,
        namespace_name,
        subscription_id,
        dont_load_clusters,
        unit_provide,
      })
    );
    let lists: response_type[][] = [];
    // 新建接收器，接收從web worker傳來的訊息事件
    const listener: Function = (e: MessageEvent): void => {
      switch (e.data) {
        case "end":
          wasm.removeEventListener("message", listener);
          resolve(lists);
          break;

        default:
          const list: response_type[] = ((): response_type[] => {
            const data = e.data;
            try {
              return typeof data === "string" ? JSON.parse(e.data) : data;
            } catch {
              return data;
            }
          })();

          if ((list || [])[0]?.kind === `${role}_info`) set_cluster_info_display && set_cluster_info_display(((list || [])[0]?.items as unknown) as string[]);
          else if (list) {
            for (let i = 0; i < list.length; i++) {
              const item = list[i];
              // 判斷回傳資料的類別
              if (item && item.kind && !item.code) {
                const func_id: number = funcs?.findIndex((func) => (item && item.kind ? item.kind === func : -2));
                if (func_id >= 0) {
                  if (item !== null) {
                    // 找到對應類別後，更新該類別狀態 (如：workspaces => set_workspaces, namespaces => set_namespaces....)
                    (funcs[func_id + 1] as React.Dispatch<
                      React.SetStateAction<
                        | workspaces_type_item[]
                        | quota_type["items"]
                        | namespaces_type_item[]
                        | nodes_type["items"]
                        | cluster_info_type
                        | node_info_type
                        | namespace_info_type_new
                        | workspace_info_type
                        | dashboard_item[]
                        | storage_classes_type_item[]
                        | service_components_type_item[]
                        | left_menu_item[]
                      >
                    >)(((item?.hasOwnProperty("items") && item?.items) || item) as namespaces_type_item[]);
                    lists[func_id] = list;
                  }
                }
              }
            }
          }
      }
    };
    wasm.addEventListener("message", listener);

    // 將web worker和Listener存下來，切換頁面時全部銷毀
    workers.push({ key: func_name, worker: wasm, listener });
  });
}

// HTTP 所有請求
// 參數說明：

// 必填：
// key：識別用的標籤，可自訂名稱，供 API 回傳值處理時識別用
// func：API 回傳值需要更新在哪個列表，該列表 context 的 set 函數
// url: 呼叫的路徑，若為陣列即批次呼叫
// method: HTTP 呼叫方法 (0 = GET, 1 = POST, 2 = PUT, 3 = DELETE)
// (若為 GET 即為該 API 的回傳值，若為 POST、PUT、DELETE 則在處理完後會自動獲取該列表最新資料)
// role: 目前的角色名稱
// subscription_id: 目前的訂閱號 ID
// datacenter_name: 目前的 datacneter 名稱 (datacentercode)
// cluster_name: 目前的集群名稱

// 選填：
// body：Request body，通常是 POST 才需要
// old_data：需要做合併處理的資料
// no_refresh: 增刪改後，不要重整(刷新)列表
// no_message: 調用完API不要顯示成功/錯誤訊息
// no_info: 不要載入info部分

// item_name: POST、PUT、DELETE 的項目名稱（成功／錯誤訊息顯示用）
// （如：workspace、namespace、workspace quota）

// action_name: 自訂的 POST、PUT、DELETE 執行動作（成功／錯誤訊息顯示用）
// （如：created, updated, deleted, assigned）

// workspace_name: 目前的 workspace 名稱
// cluster_session: sessionStorage 裡的集群名稱（user_info用）
// workspace_session: sessionStorage 裡的 workspace 名稱（user_info用）
// index: 獲取 cluster/workspace/namespace/node info 後，寫入原 cluster/workspace/namespace/node 列表
// no_cache: 不要載入快取部分

export function http_request(
  key: string,
  // props: { log_out: log_out_type; history: any; redirect_url: string },
  props: any,
  func:
    | undefined
    | React.Dispatch<React.SetStateAction<clusters_type_item[]>>
    | React.Dispatch<React.SetStateAction<workspaces_type_item[] | undefined>>
    | React.Dispatch<React.SetStateAction<app_item[]>>
    | React.Dispatch<React.SetStateAction<metric_values_new>>
    | React.Dispatch<React.SetStateAction<quota_type_item[]>>
    | React.Dispatch<React.SetStateAction<namespaces_type_item[] | undefined>>
    | React.Dispatch<React.SetStateAction<nodes_type_item[]>>
    | React.Dispatch<React.SetStateAction<cluster_info_type>>
    | React.Dispatch<React.SetStateAction<node_info_type>>
    | React.Dispatch<React.SetStateAction<namespace_info_type_new>>
    | React.Dispatch<React.SetStateAction<workspace_info_type>>
    | React.Dispatch<React.SetStateAction<storage_classes_type_item[]>>
    | React.Dispatch<React.SetStateAction<service_components_type_item[]>>
    | React.Dispatch<React.SetStateAction<dashboard_item[]>>
    | React.Dispatch<React.SetStateAction<workload_type[] | undefined>>
    | React.Dispatch<React.SetStateAction<pods_type_item[] | undefined>>
    | any,
  url: string | string[],
  method: number,
  role: string | string[] | undefined,
  subscription_id: string | undefined,
  datacenter_name: string,
  cluster_name: string,
  body?: object,
  old_data?: any[],
  no_refresh?: boolean,
  no_message?: boolean,
  no_info?: boolean,
  item_name?: string,
  action_name?: string,
  workspace_name?: string,
  cluster_session?: string,
  workspace_session?: string,
  index?: number,
  no_cache?: boolean,
  subscription_id_org?: string,
  unit_provide?: string,
  set_cluster_info_display?: React.Dispatch<React.SetStateAction<string[] | undefined>>
): Promise<unknown> {
  destroy_worker(key);
  return new Promise((resolve: (value?: unknown) => void): void => {
    // 初始化 Web Worker
    const wasm: worker_type = new WebWorker();
    // 傳遞訊息給 Web Worker，告訴 Web Worker 開始執行
    wasm.postMessage(
      JSON.stringify({
        url,
        origin: window.location.origin,
        method,
        role,
        datacenter_name,
        cluster_name,
        workspace_name,
        body: JSON.stringify(body),
        key,
        old_data,
        no_info,
        no_refresh,
        subscription_id,
        cluster_session,
        workspace_session,
        index,
        no_cache,
        subscription_id_org,
        unit_provide,
        set_cluster_info_display,
      })
    );
    let first_response: response_type;
    // 新建接收器，接收從web worker傳來的訊息事件
    const listener: Function = (e: MessageEvent): void => {
      switch (e.data) {
        case "done":
          resolve(first_response);
          break;

        case "end":
          wasm.removeEventListener("message", listener);
          resolve(first_response);
          break;

        default:
          const response = ((): [response_type, boolean] => {
            const data = e.data;
            try {
              return typeof data === "string" ? JSON.parse(e.data) : data;
            } catch {
              return data;
            }
          })(),
            response_new: response_type = Array.isArray(response) ? response[0] : ((response as unknown) as response_type);

          if (response_new) {
            // if(response_new.code === 401 && key !== "subscriptions") {
            //   key === "user_info" && openNotification("error", response_new.reason && response_new.reason, response_new.message || "");
            //   log_out(props)
            // }
            if(response_new.code === 403 && key === "user_info") {
              openNotification("error", response_new.reason && response_new.reason, response_new.message || "");
            }
            // response_new.code === 401 && key !== "subscriptions" && log_out(undefined, datacenter_name);
            // response_new.code === 401 && key !== "subscriptions" && props.log_out?.(props, undefined, props.history, props.redirect_url);
            response_new.code === 401 && key !== "subscriptions" && log_out({props, env: undefined, history: props.history, redirect_url: "/login/sign-in"});
            const path: string = window.location.pathname;
            const path_to_service: string[] = [
              "Workspace",
              "/cluster-info/workspaces",
              "Namespace",
              "/workspace-info/namespaces",
              "Workspace quota",
              "/quotas/workspace-quotas",
              "Namespace quota",
              "/quotas/namespace-quotas",
              // "Workspace scaled",
              // "/workspace/scale",
              // "Namespace scaled",
              // "/namespace/scale"
              "Application",
              "/applications",
            ];

            // @ts-ignore
            if ((!response_new.code || (response_new && response_new.code < 400)) && func && (method === 0 || first_response)) {
              response_new.kind === `${role}_info`
                ? set_cluster_info_display && set_cluster_info_display((response_new.items as unknown) as string[])
                : (func as React.Dispatch<
                  React.SetStateAction<
                    | workspaces_type_item[]
                    | quota_type["items"]
                    | namespaces_type_item[]
                    | nodes_type["items"]
                    | cluster_info_type
                    | node_info_type
                    | namespace_info_type_new
                    | workspace_info_type
                    | dashboard_item[]
                    | storage_classes_type_item[]
                    | service_components_type_item[]
                  >
                >)(((response_new?.hasOwnProperty("items") && response_new?.items) || response_new) as namespaces_type_item[]);
            }

            if (!first_response) {
              if (method > 0 && !no_message) {
                const actions: string[] = ["created", "updated", "deleted", action_name || ""],
                  index_of_service: number = path_to_service.findIndex((p) => p === path);
                if (response_new) {
                  // @ts-ignore
                  if (response_new.code && response_new.code >= 400) {
                    key === 'cluster_init' ?
                      openNotification("error", "Error", response_new.content || "") :
                      openNotification("error", response_new.reason && response_new.reason, response_new.message || "");
                  } else {
                    key === 'cluster_init' ?
                      openNotification(
                        "success",
                        "Successfully",
                        `${response_new?.content}`
                      ) : key !== "login" && key !== 'cluster_init' &&
                      openNotification(
                        "success",
                        "Successfully",
                        `${index_of_service !== -1 ? path_to_service[index_of_service - 1] : ""} ${item_name ? item_name : ""} ${
                        actions[method - 1]
                        } successfully`
                      );
                  }
                } else {
                  key === 'cluster_init' ?
                    openNotification(
                      "error",
                      "Error",
                      `cluster initialization error`
                    ) :
                    openNotification(
                      "error",
                      "Error",
                      `${index_of_service !== -1 ? path_to_service[index_of_service - 1] : ""} ${item_name ? item_name : ""} ${actions[method - 1]} error`
                    );
                }

                // response.code && response.code >= 400 ? message.error(response.reason && response.reason.response.status.message) : message.success(`${actions[method - 1]} Succesfully`);
              }

              // @ts-ignore
              (!response_new.code || response_new.code < 400) && !response[1] && (first_response = response_new);
            }
          }
      }
    };
    wasm.addEventListener("message", listener);

    // 將web worker和Listener存下來，切換頁面時全部銷毀
    workers.push({ key, worker: wasm, listener });
  });
}

// 銷毀 Web Worker
// 頁面切換或變動時，需要銷毀所有或特定類別的 Web Worker，以免滯留記憶體造成 Memory Leak 問題

export function destroy_worker(key_selected?: string): void {
  for (let i = 0; i < workers.length; i++) {
    const { key, worker, listener } = workers[i];
    if (!key_selected || (key_selected && key === key_selected)) {
      worker.removeEventListener("message", listener);
      worker.terminate();
    }
  }
}
